import { useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/auth'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Field } from '../components/ui/Field'
import { Modal } from '../components/ui/Modal'
import { QuickDateRange, currentYearRange } from '../components/ui/DateField'

interface BankAccount {
  id: number
  bank: string
  iban: string
  currency: string
  name: string | null
}

interface MatchedInvoice {
  id: number
  docNo: string
  docType: string
  date: string
  totalWithVat: string | number | null
}

interface BankTransaction {
  id: number
  accountId: number
  bookingDate: string
  valueDate: string | null
  amount: string | number
  currency: string
  partnerName: string | null
  partnerIban: string | null
  partnerCif: string | null
  description: string | null
  reference: string | null
  reconStatus: 'pending' | 'matched' | 'manual' | 'no_match' | 'ignored'
  matchedInvoiceId: number | null
  matchedAt: string | null
  matchCandidates: MatchCandidate[] | null
  account: { id: number; bank: string; iban: string }
  matchedInvoice: MatchedInvoice | null
  payerKind: PayerKind | null
  client: { id: number; name: string } | null
}

type PayerKind = 'client' | 'online' | 'intern' | 'unknown' | 'furnizor' | 'angajat' | 'stat'

/** Cardurile de sus: „Fără factură" se împarte după cum s-a găsit sau nu partenerul. */
type Grup = BankTransaction['reconStatus'] | 'partener' | 'neidentificat'

interface MatchCandidate {
  invoiceId: number
  docNo: string
  docType: string
  date: string
  partnerName: string | null
  partnerCif: string | null
  totalWithVat: number
  confidence: number
  reasons: string[]
}

interface BankImport {
  id: number
  fileName: string
  fileFormat: string
  importedAt: string
  txTotal: number
  txInserted: number
  txDuplicates: number
  periodStart: string | null
  periodEnd: string | null
  parseErrors: string[]
  account: { id: number; bank: string; iban: string; name: string | null } | null
  importedBy: { id: number; name: string } | null
}

/**
 * Etichetele stărilor de reconciliere.
 *
 * „Negăsită", scris cu roșu, arăta ca o defecțiune — 1.810 tranzacții „negăsite" citite ca 1.810
 * probleme. Nu erau: o tranzacție bancară nu trebuie să aibă o factură. Salariile, taxele,
 * comisioanele și transferurile între conturile proprii n-au ce potrivi, iar o plată parțială nu
 * se potrivește nici ea, fiindcă potrivirea cere suma exactă a facturii.
 *
 * Deci starea se numește după ce ESTE — n-am găsit o factură pentru ea — și nu mai e roșie.
 * Roșul se păstrează pentru ce chiar cere intervenție.
 */
const STATUS_LABELS: Record<BankTransaction['reconStatus'], string> = {
  pending: 'Neverificate',
  matched: 'Potrivite automat',
  manual: 'Potrivite manual',
  no_match: 'Fără factură',
  ignored: 'Ignorate',
}

/**
 * „O plată nu trebuie legată neapărat de o factură, ci de un partener" (Alexandru, 14.10.2026).
 * Încasarea FARMATOP închidea șase facturi deodată și apărea „Fără factură", deși clientul era
 * recunoscut. Acum, fără factură potrivită, rândul arată cine e partenerul; „Neidentificat"
 * rămâne doar pentru banii despre care chiar nu știm de la cine sunt sau cui au plecat.
 */
const GRUP_CARDURI: Grup[] = [
  'pending',
  'matched',
  'manual',
  'partener',
  'neidentificat',
  'ignored',
]
const GRUP_LABELS: Record<Grup, string> = {
  ...STATUS_LABELS,
  partener: 'Partener identificat',
  neidentificat: 'Neidentificate',
}

const PARTENER_LABELS: Record<Exclude<PayerKind, 'unknown'>, string> = {
  client: 'Client',
  furnizor: 'Furnizor',
  angajat: 'Angajat',
  stat: 'Stat (taxe)',
  intern: 'Intern / bancă',
  online: 'Online / curier',
}

const STATUS_COLORS: Record<BankTransaction['reconStatus'], string> = {
  pending: 'bg-b4l-amber-light text-b4l-amber-dark',
  matched: 'bg-b4l-green-light text-b4l-green-dark',
  manual: 'bg-b4l-blue-light text-b4l-blue-dark',
  no_match: 'bg-b4l-bg text-b4l-text-muted',
  ignored: 'bg-b4l-bg text-b4l-text-dim',
}

export function IncasariPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canRun = permissions.includes('compliance:run')

  const [statusFilter, setStatusFilter] = useState<Grup | ''>('')
  const [accountFilter, setAccountFilter] = useState<string>('')
  /** Categoria partenerului, în interiorul „Partener identificat" (Client, Furnizor…). */
  const [kindFilter, setKindFilter] = useState<Exclude<PayerKind, 'unknown'> | ''>('')
  /** Perioada, după data tranzacției — implicit anul curent, ca pe celelalte pagini. */
  const [dataDe, setDataDe] = useState(() => currentYearRange().from)
  const [dataPana, setDataPana] = useState(() => currentYearRange().to)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [createAccountOpen, setCreateAccountOpen] = useState(false)
  const [activeTx, setActiveTx] = useState<BankTransaction | null>(null)
  const [importToDelete, setImportToDelete] = useState<BankImport | null>(null)

  const accountsQuery = useQuery<{ accounts: BankAccount[] }>({
    queryKey: ['banking-accounts'],
    queryFn: async () => (await api.get<{ accounts: BankAccount[] }>('/banking/accounts')).data,
  })
  const accounts = accountsQuery.data?.accounts ?? []

  const importsQuery = useQuery<{ imports: BankImport[] }>({
    queryKey: ['banking-imports'],
    queryFn: async () => (await api.get<{ imports: BankImport[] }>('/banking/imports')).data,
  })
  const imports = importsQuery.data?.imports ?? []

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    if (kindFilter) {
      p.set('status', 'partener')
      p.set('payerKind', kindFilter)
    } else if (statusFilter) p.set('status', statusFilter)
    if (accountFilter) p.set('accountId', accountFilter)
    if (search.trim()) p.set('search', search.trim())
    if (dataDe) p.set('dateFrom', dataDe)
    if (dataPana) p.set('dateTo', dataPana)
    p.set('limit', '500')
    return p.toString()
  }, [statusFilter, kindFilter, accountFilter, search, dataDe, dataPana])

  const txQuery = useQuery<{
    transactions: BankTransaction[]
    total: number
    statusCounts: Record<string, number>
  }>({
    queryKey: ['banking-transactions', queryString],
    queryFn: async () => (await api.get(`/banking/transactions?${queryString}`)).data,
  })
  const transactions = txQuery.data?.transactions ?? []
  const totalTx = txQuery.data?.total ?? 0
  const statusCounts = txQuery.data?.statusCounts ?? {}

  // Upload handler
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadAccountId, setUploadAccountId] = useState<string>('')

  /*
    Extrasul respins ca posibila dublura: il tinem minte cu mesajul serverului, ca omul sa
    poata confirma in cunostinta de cauza. A doua incercare pleaca cu `force`.
  */
  const [dublura, setDublura] = useState<{ file: File; mesaj: string } | null>(null)

  const uploadMutation = useMutation({
    mutationFn: async ({ file, force }: { file: File; force?: boolean }) => {
      if (!uploadAccountId) throw new Error('Selectează contul')
      const fd = new FormData()
      fd.append('file', file)
      fd.append('accountId', uploadAccountId)
      if (force) fd.append('force', '1')
      const res = await api.post('/banking/imports', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data as {
        stats: {
          parsed: number
          inserted: number
          duplicates: number
          autoMatched: number
          parseErrors: number
        }
      }
    },
    onSuccess: (data) => {
      toast.success(
        `Import reușit: ${data.stats.inserted} tranzacții noi, ${data.stats.autoMatched} potrivite automat, ${data.stats.duplicates} duplicate`,
      )
      setDublura(null)
      queryClient.invalidateQueries({ queryKey: ['banking-transactions'] })
      queryClient.invalidateQueries({ queryKey: ['banking-imports'] })
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    onError: (e: unknown, vars) => {
      const r = (e as { response?: { status?: number; data?: { error?: { message?: string } } } })
        .response
      // 409 = extrasul pare deja incarcat. Nu e o eroare, e o intrebare.
      if (r?.status === 409 && !vars.force) {
        setDublura({ file: vars.file, mesaj: r.data?.error?.message ?? '' })
        return
      }
      showError(e)
    },
  })

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) uploadMutation.mutate({ file: f })
  }

  const formatAmount = (amount: string | number) => {
    const n = typeof amount === 'string' ? parseFloat(amount) : amount
    return n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Scan inbox automat
  const [scanStatusOpen, setScanStatusOpen] = useState(false)
  const scanMutation = useMutation({
    mutationFn: async () => (await api.post('/banking/scan-inbox-now')).data,
    onSuccess: () => {
      toast.success('Scanarea inboxului a pornit. Rezultatul apare în 1–2 minute.')
      setScanStatusOpen(true)
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['banking-transactions'] })
        queryClient.invalidateQueries({ queryKey: ['banking-imports'] })
        queryClient.invalidateQueries({ queryKey: ['bank-scan-status'] })
      }, 60_000)
    },
    onError: showError,
  })

  const scanStatusQuery = useQuery<{
    status: {
      startedAt: string
      finishedAt: string | null
      scanned: number
      emailsWithAttachments: number
      mt940Found: number
      importsCreated: number
      txInserted: number
      txAutoMatched: number
      duplicates: number
      errors: number
      diagnostics: string[]
    } | null
  }>({
    queryKey: ['bank-scan-status'],
    queryFn: async () => (await api.get('/banking/scan-status')).data,
    enabled: scanStatusOpen,
    refetchInterval: scanStatusOpen ? 5_000 : false,
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Extras de cont</h1>
        <div className="flex gap-2">
          {canRun && (
            <Button
              variant="secondary"
              onClick={() => scanMutation.mutate()}
              loading={scanMutation.isPending}
              title="Caută extrase MT940 în inbox și le importă automat"
            >
              Scanează inboxul
            </Button>
          )}
          {canRun && accounts.length === 0 && (
            <Button onClick={() => setCreateAccountOpen(true)}>+ Adaugă cont bancar</Button>
          )}
          <Button variant="secondary" onClick={() => setScanStatusOpen((o) => !o)}>
            {scanStatusOpen ? 'Ascunde' : 'Vezi'} ultima scanare
          </Button>
        </div>
      </div>

      {scanStatusOpen && scanStatusQuery.data && (
        <Card>
          {scanStatusQuery.data.status ? (
            <div className="space-y-2 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <strong>Ultima scanare a inboxului:</strong>
                <span>
                  Pornit: {new Date(scanStatusQuery.data.status.startedAt).toLocaleString('en-GB')}
                </span>
                {scanStatusQuery.data.status.finishedAt ? (
                  <span>
                    · Finalizat:{' '}
                    {new Date(scanStatusQuery.data.status.finishedAt).toLocaleString('en-GB')}
                  </span>
                ) : (
                  <span className="rounded bg-b4l-amber-light px-1.5 py-0.5 text-b4l-amber-dark">
                    În curs…
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 md:grid-cols-7">
                <ScanStat label="Scanate" value={scanStatusQuery.data.status.scanned} />
                <ScanStat
                  label="Cu atașament"
                  value={scanStatusQuery.data.status.emailsWithAttachments}
                />
                <ScanStat label="MT940" value={scanStatusQuery.data.status.mt940Found} />
                <ScanStat
                  label="Importuri"
                  value={scanStatusQuery.data.status.importsCreated}
                  accent="green"
                />
                <ScanStat
                  label="Tranzacții"
                  value={scanStatusQuery.data.status.txInserted}
                  accent="green"
                />
                <ScanStat
                  label="Match auto"
                  value={scanStatusQuery.data.status.txAutoMatched}
                  accent="green"
                />
                <ScanStat
                  label="Erori"
                  value={scanStatusQuery.data.status.errors}
                  accent={scanStatusQuery.data.status.errors > 0 ? 'red' : undefined}
                />
              </div>
              {scanStatusQuery.data.status.diagnostics.length > 0 && (
                <div className="rounded border border-b4l-amber-light bg-b4l-amber-light/20 p-2">
                  <strong>Diagnostic:</strong>
                  <ul className="ml-4 mt-1 list-disc">
                    {scanStatusQuery.data.status.diagnostics.map((d, i) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-b4l-text-muted">
              Nicio scanare încă. Apasă „Scanează inboxul” sau așteaptă rularea automată (la 30 de
              minute).
            </p>
          )}
        </Card>
      )}

      {/* Stats banner */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        {GRUP_CARDURI.map((s) => {
          const count = statusCounts[s] ?? 0
          const active = statusFilter === s
          return (
            <button
              key={s}
              onClick={() => {
                setKindFilter('')
                setStatusFilter(active ? '' : s)
              }}
              className={`rounded-lg border-2 px-3 py-2 text-left ${active ? 'border-b4l-text bg-b4l-bg-soft' : 'border-b4l-border bg-white hover:bg-b4l-bg-soft'}`}
            >
              <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">
                {GRUP_LABELS[s]}
              </div>
              <div className="text-2xl font-bold">{count}</div>
            </button>
          )
        })}
      </div>

      <p className="text-xs text-b4l-text-muted">
        <strong>O plată se leagă de un partener, nu neapărat de o factură.</strong> O încasare care
        închide mai multe facturi deodată, o tranșă, un salariu sau o taxă n-au o factură cu exact
        aceeași sumă — de aceea, când nu există una, rândul arată cine e partenerul: client,
        furnizor, angajat, stat, intern / bancă sau online / curier. De urmărit sunt doar cele{' '}
        <strong>Neidentificate</strong>: bani despre care nu știm de la cine au venit sau cui au
        plecat. Soldurile de încasat și de plătit nu se calculează de aici, ci din contabilitate.
      </p>

      {/* Upload */}
      {canRun && accounts.length > 0 && (
        <Card title="Import extras MT940">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Cont</label>
              <select
                value={uploadAccountId}
                onChange={(e) => setUploadAccountId(e.target.value)}
                className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
              >
                <option value="">— alege cont —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.bank} — {a.iban} {a.name ? `(${a.name})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".sta,.mt940,.txt"
                onChange={onFileChange}
                disabled={!uploadAccountId || uploadMutation.isPending}
                className="text-sm"
              />
              <Button
                onClick={() => setCreateAccountOpen(true)}
                variant="secondary"
                className="!px-2 !py-1 !text-xs"
              >
                + Cont nou
              </Button>
            </div>
          </div>
          {uploadMutation.isPending && (
            <p className="mt-2 text-xs text-b4l-text-muted">Se procesează fișierul...</p>
          )}
        </Card>
      )}

      {/* Imports istoric */}
      {imports.length > 0 && (
        <Card title="Importuri recente">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-b4l-border text-left uppercase tracking-wide text-b4l-text-muted">
                <th className="py-1.5 pr-3">Data</th>
                <th className="py-1.5 pr-3">Fișier</th>
                <th className="py-1.5 pr-3">Cont</th>
                <th className="py-1.5 pr-3">Tranzacții</th>
                <th className="py-1.5 pr-3">User</th>
                <th className="py-1.5 pr-3" />
              </tr>
            </thead>
            <tbody>
              {imports.map((imp) => (
                <tr key={imp.id} className="border-b border-b4l-border last:border-0">
                  <td className="py-1.5 pr-3">
                    {new Date(imp.importedAt).toLocaleString('en-GB')}
                  </td>
                  <td className="py-1.5 pr-3 font-mono">{imp.fileName}</td>
                  <td className="py-1.5 pr-3">
                    {imp.account ? `${imp.account.bank} ${imp.account.iban.slice(-6)}` : '—'}
                  </td>
                  <td className="py-1.5 pr-3">
                    {imp.txInserted} noi /{' '}
                    <span className="text-b4l-text-dim">{imp.txDuplicates} dup</span>
                  </td>
                  <td className="py-1.5 pr-3 text-b4l-text-muted">
                    {imp.importedBy?.name ?? 'Sistem (cron)'}
                  </td>
                  <td className="py-1.5 pr-3 text-right">
                    <button
                      type="button"
                      onClick={() => setImportToDelete(imp)}
                      className="text-b4l-red hover:underline"
                      title="Șterge importul și tranzacțiile lui — util când extrasul a fost citit greșit"
                    >
                      Șterge
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Filtre + lista tranzactii */}
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setSearch(searchInput)
            }}
            onBlur={() => setSearch(searchInput)}
            placeholder="Caută partener / CIF / descriere... (Enter)"
            className="w-64 rounded border border-b4l-border px-3 py-1.5"
          />
          <select
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            className="rounded border border-b4l-border bg-white px-2 py-1.5"
          >
            <option value="">Toate conturile</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.bank} {a.iban.slice(-6)}
              </option>
            ))}
          </select>
          <select
            value={kindFilter ? `k:${kindFilter}` : statusFilter}
            onChange={(e) => {
              const v = e.target.value
              if (v.startsWith('k:')) {
                setStatusFilter('')
                setKindFilter(v.slice(2) as Exclude<PayerKind, 'unknown'>)
              } else {
                setKindFilter('')
                setStatusFilter(v as Grup | '')
              }
            }}
            className="rounded border border-b4l-border bg-white px-2 py-1.5"
            aria-label="Status"
          >
            <option value="">Toate stările</option>
            {GRUP_CARDURI.map((g) => (
              <option key={g} value={g}>
                {GRUP_LABELS[g]}
              </option>
            ))}
            <optgroup label="Partener identificat — pe categorii">
              {(Object.keys(PARTENER_LABELS) as Array<Exclude<PayerKind, 'unknown'>>).map((k) => (
                <option key={k} value={`k:${k}`}>
                  {PARTENER_LABELS[k]}
                </option>
              ))}
            </optgroup>
          </select>
          <span className="ml-auto text-xs text-b4l-text-muted">
            {totalTx > transactions.length
              ? `primele ${transactions.length} din ${totalTx} tranzacții`
              : `${transactions.length} tranzacții`}
          </span>
        </div>
        <div className="mb-3">
          <QuickDateRange
            fromValue={dataDe}
            toValue={dataPana}
            onChange={(f, t) => {
              setDataDe(f)
              setDataPana(t)
            }}
          />
        </div>

        {txQuery.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : transactions.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Nicio tranzacție.{' '}
            {canRun && accounts.length === 0
              ? 'Începe prin a adăuga un cont bancar și a urca un extras MT940.'
              : 'Urcă un extras MT940 pentru a începe.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">Data</th>
                <th className="py-2 pr-3 font-medium">Partener</th>
                <th className="py-2 pr-3 font-medium">Sumă</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Factură</th>
                <th className="py-2 pr-3 font-medium">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => {
                const amount = typeof t.amount === 'string' ? parseFloat(t.amount) : t.amount
                const isCredit = amount > 0
                return (
                  <tr key={t.id} className="border-b border-b4l-border last:border-0 align-top">
                    <td className="py-2 pr-3 text-xs">
                      {new Date(t.bookingDate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="font-medium">
                        {t.partnerName ?? <span className="text-b4l-text-dim">—</span>}
                      </div>
                      {t.partnerCif && (
                        <div className="text-[10px] text-b4l-text-dim">{t.partnerCif}</div>
                      )}
                      {t.description && (
                        <div className="mt-0.5 line-clamp-1 text-xs text-b4l-text-muted">
                          {t.description}
                        </div>
                      )}
                    </td>
                    <td
                      className={`py-2 pr-3 text-right text-sm font-semibold ${isCredit ? 'text-b4l-green-dark' : 'text-b4l-red-dark'}`}
                    >
                      {isCredit ? '+' : ''}
                      {formatAmount(amount)} {t.currency}
                    </td>
                    <td className="py-2 pr-3">
                      <StatusPartener t={t} />
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {t.matchedInvoice ? (
                        <span className="font-mono">{t.matchedInvoice.docNo}</span>
                      ) : (
                        <span className="text-b4l-text-dim">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <Button
                        variant="secondary"
                        onClick={() => setActiveTx(t)}
                        className="!px-2 !py-1 !text-xs"
                      >
                        Detalii
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>

      {dublura && (
        <Modal
          open
          onClose={() => setDublura(null)}
          title="Extrasul pare deja încărcat"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setDublura(null)
                  if (fileInputRef.current) fileInputRef.current.value = ''
                }}
              >
                Renunță
              </Button>
              <Button
                variant="danger"
                loading={uploadMutation.isPending}
                onClick={() => uploadMutation.mutate({ file: dublura.file, force: true })}
              >
                Încarcă oricum
              </Button>
            </>
          }
        >
          <p className="text-sm text-b4l-text">{dublura.mesaj}</p>
          <p className="mt-2 text-xs text-b4l-text-muted">
            Fișier: <span className="font-mono">{dublura.file.name}</span>
          </p>
        </Modal>
      )}

      {importToDelete && (
        <DeleteImportModal
          imp={importToDelete}
          onClose={() => setImportToDelete(null)}
          onDeleted={() => {
            setImportToDelete(null)
            queryClient.invalidateQueries({ queryKey: ['banking-imports'] })
            queryClient.invalidateQueries({ queryKey: ['banking-transactions'] })
          }}
        />
      )}

      {createAccountOpen && (
        <CreateAccountModal
          onClose={() => setCreateAccountOpen(false)}
          onCreated={() => {
            setCreateAccountOpen(false)
            queryClient.invalidateQueries({ queryKey: ['banking-accounts'] })
          }}
        />
      )}

      {activeTx && (
        <TxDetailModal
          tx={activeTx}
          canMatch={canRun}
          onClose={() => setActiveTx(null)}
          onUpdated={() => {
            queryClient.invalidateQueries({ queryKey: ['banking-transactions'] })
            setActiveTx(null)
          }}
        />
      )}
    </div>
  )
}

function showError(err: unknown) {
  const m =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ??
    (err as Error)?.message ??
    'Eroare'
  toast.error(m)
}

/**
 * Stergerea unui import. Necesara cand extrasul a fost citit gresit — reparatiile din
 * parser se aplica doar la import, nu retroactiv, deci fisierul trebuie reluat.
 * Aratam cate tranzactii sunt deja legate de facturi, pentru ca acelea se pierd.
 */
function DeleteImportModal({
  imp,
  onClose,
  onDeleted,
}: {
  imp: BankImport
  onClose: () => void
  onDeleted: () => void
}) {
  const impact = useQuery<{ total: number; matched: number }>({
    queryKey: ['banking-import-impact', imp.id],
    queryFn: async () => (await api.get(`/banking/imports/${imp.id}/impact`)).data,
  })

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/banking/imports/${imp.id}`)).data,
    onSuccess: (d: { deletedTransactions: number }) => {
      toast.success(`Import șters — ${d.deletedTransactions} tranzacții eliminate`)
      onDeleted()
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message || 'Nu am putut șterge importul',
      ),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title="Ștergi importul?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button variant="danger" loading={del.isPending} onClick={() => del.mutate()}>
            Șterge importul
          </Button>
        </>
      }
    >
      <p className="text-sm">
        Se șterg <strong>{impact.data?.total ?? imp.txInserted}</strong> tranzacții din{' '}
        <strong>{imp.fileName}</strong>.
      </p>
      {(impact.data?.matched ?? 0) > 0 && (
        <p className="mt-2 rounded border border-b4l-amber-dark/40 bg-amber-50 p-2 text-sm text-b4l-amber-dark">
          Atenție: <strong>{impact.data?.matched}</strong> dintre ele sunt deja legate de facturi.
          Legăturile se pierd și trebuie refăcute după reimport.
        </p>
      )}
      <p className="mt-2 text-sm text-b4l-text-muted">
        Fișierul rămâne în email. După ștergere, apasă „Scanează inbox acum” ca să-l reimporte — de
        data asta citit corect.
      </p>
    </Modal>
  )
}

function CreateAccountModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [bank, setBank] = useState('ING')
  const [iban, setIban] = useState('')
  const [name, setName] = useState('')
  const mut = useMutation({
    mutationFn: async () =>
      (await api.post('/banking/accounts', { bank, iban: iban.replace(/\s+/g, ''), name })).data,
    onSuccess: () => {
      toast.success('Cont adăugat')
      onCreated()
    },
    onError: showError,
  })
  return (
    <Modal
      open
      onClose={onClose}
      title="Cont bancar nou"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => mut.mutate()} loading={mut.isPending}>
            Salvează
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Bancă" value={bank} onChange={(e) => setBank(e.target.value)} />
        <Field
          label="IBAN"
          value={iban}
          onChange={(e) => setIban(e.target.value)}
          placeholder="RO12INGB..."
        />
        <Field
          label="Nume (opțional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ex: ING - cont curent RON"
        />
      </div>
    </Modal>
  )
}

function TxDetailModal({
  tx,
  canMatch,
  onClose,
  onUpdated,
}: {
  tx: BankTransaction
  canMatch: boolean
  onClose: () => void
  onUpdated: () => void
}) {
  const candidatesQuery = useQuery<{ candidates: MatchCandidate[] }>({
    queryKey: ['tx-candidates', tx.id],
    queryFn: async () =>
      (await api.get<{ candidates: MatchCandidate[] }>(`/banking/transactions/${tx.id}/candidates`))
        .data,
    enabled: tx.reconStatus !== 'matched' && tx.reconStatus !== 'manual',
  })

  const matchMutation = useMutation({
    mutationFn: async (invoiceId: number) =>
      (await api.post(`/banking/transactions/${tx.id}/match`, { invoiceId })).data,
    onSuccess: () => {
      toast.success('Tranzacție asociată')
      onUpdated()
    },
    onError: showError,
  })

  const unmatchMutation = useMutation({
    mutationFn: async () => (await api.post(`/banking/transactions/${tx.id}/unmatch`)).data,
    onSuccess: () => {
      toast.success('Asociere anulată')
      onUpdated()
    },
    onError: showError,
  })

  const ignoreMutation = useMutation({
    mutationFn: async () => (await api.post(`/banking/transactions/${tx.id}/ignore`)).data,
    onSuccess: () => {
      toast.success('Tranzacție ignorată')
      onUpdated()
    },
    onError: showError,
  })

  const amount = typeof tx.amount === 'string' ? parseFloat(tx.amount) : tx.amount
  const candidates = candidatesQuery.data?.candidates ?? []

  return (
    <Modal
      open
      onClose={onClose}
      title="Detalii tranzacție"
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Închide
        </Button>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
          <div className="grid grid-cols-2 gap-3">
            <Detail label="Data" value={new Date(tx.bookingDate).toLocaleDateString('en-GB')} />
            <Detail
              label="Sumă"
              value={`${amount > 0 ? '+' : ''}${amount.toLocaleString('ro-RO', { minimumFractionDigits: 2 })} ${tx.currency}`}
            />
            <Detail label="Partener" value={tx.partnerName ?? '—'} />
            <Detail label="CIF" value={tx.partnerCif ?? '—'} />
            <Detail label="IBAN partener" value={tx.partnerIban ?? '—'} />
            <Detail label="Referință" value={tx.reference ?? '—'} />
          </div>
          {tx.description && (
            <div className="mt-2 border-t border-b4l-border pt-2 text-xs">
              <div className="mb-0.5 font-medium text-b4l-text-dim">Descriere</div>
              {tx.description}
            </div>
          )}
        </div>

        {tx.matchedInvoice ? (
          <div className="rounded border border-b4l-green-light bg-b4l-green-light/20 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-b4l-green-dark">
              Asociată cu factura
            </div>
            <div className="mt-1 font-mono">{tx.matchedInvoice.docNo}</div>
            <div className="text-xs text-b4l-text-muted">
              {new Date(tx.matchedInvoice.date).toLocaleDateString('en-GB')} ·{' '}
              {tx.matchedInvoice.docType}
            </div>
            {canMatch && (
              <Button
                variant="secondary"
                onClick={() => unmatchMutation.mutate()}
                className="mt-2 !px-2 !py-1 !text-xs"
              >
                Anulează asocierea
              </Button>
            )}
          </div>
        ) : (
          <div>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-b4l-text-dim">
              Candidați factură
            </h4>
            {candidatesQuery.isLoading && <p className="text-xs">Se caută...</p>}
            {candidates.length === 0 && !candidatesQuery.isLoading && (
              <p className="text-xs text-b4l-text-muted">
                Niciun candidat găsit (suma sau intervalul de date nu se potrivesc cu nicio
                factură).
              </p>
            )}
            <div className="space-y-1.5">
              {candidates.map((c) => (
                <div
                  key={c.invoiceId}
                  className="flex items-center justify-between rounded border border-b4l-border bg-white p-2 text-xs"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold">{c.docNo}</span>
                      <span className="text-b4l-text-dim">{c.docType}</span>
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${c.confidence >= 90 ? 'bg-b4l-green-light text-b4l-green-dark' : c.confidence >= 60 ? 'bg-b4l-amber-light text-b4l-amber-dark' : 'bg-b4l-bg text-b4l-text-muted'}`}
                      >
                        {c.confidence}%
                      </span>
                    </div>
                    <div className="text-b4l-text-muted">
                      {c.partnerName} · {new Date(c.date).toLocaleDateString('en-GB')} ·{' '}
                      {c.totalWithVat} RON
                    </div>
                    <div className="text-[10px] text-b4l-text-dim">{c.reasons.join(' · ')}</div>
                  </div>
                  {canMatch && (
                    <Button
                      onClick={() => matchMutation.mutate(c.invoiceId)}
                      loading={matchMutation.isPending}
                      className="!px-2 !py-1 !text-xs"
                    >
                      Asociază
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {canMatch && tx.reconStatus !== 'ignored' && (
              <Button
                variant="secondary"
                onClick={() => ignoreMutation.mutate()}
                className="mt-3 !px-2 !py-1 !text-xs"
              >
                Ignoră tranzacția
              </Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

function ScanStat({
  label,
  value,
  accent,
}: {
  label: string
  value: number
  accent?: 'green' | 'red'
}) {
  const color =
    accent === 'green'
      ? 'text-b4l-green-dark'
      : accent === 'red'
        ? 'text-b4l-red-dark'
        : 'text-b4l-text'
  return (
    <div className="rounded border border-b4l-border bg-b4l-bg-soft p-2">
      <div className="text-[10px] uppercase tracking-wider text-b4l-text-dim">{label}</div>
      <div className={`text-base font-semibold ${color}`}>{value}</div>
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">{label}</div>
      <div className="break-words">{value}</div>
    </div>
  )
}

/** Starea rândului; la „Fără factură", cine e partenerul (sau că nu-l știm). */
function StatusPartener({ t }: { t: BankTransaction }) {
  if (t.reconStatus !== 'no_match') {
    return (
      <span className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[t.reconStatus]}`}>
        {STATUS_LABELS[t.reconStatus]}
      </span>
    )
  }
  if (!t.payerKind || t.payerKind === 'unknown') {
    return (
      <span className="rounded bg-b4l-amber-light px-2 py-0.5 text-[11px] text-b4l-amber-dark">
        Neidentificat
      </span>
    )
  }
  return (
    <div>
      <span className="rounded bg-b4l-blue-light px-2 py-0.5 text-[11px] text-b4l-blue-dark">
        {PARTENER_LABELS[t.payerKind]}
      </span>
      {t.client && <div className="mt-0.5 text-[10px] text-b4l-text-muted">{t.client.name}</div>}
    </div>
  )
}
